import { api } from './client';
import type { AuthResponse, User } from './types';

// Normaliserar API-svar till en konsekvent User-form med displayName.
function normalizeUser(user: User): User {
  return {
    ...user,
    displayName:
      user.displayName ||
      user.name.charAt(0).toUpperCase() + user.name.slice(1),
  };
}

// Wrap-tolerant: stöd både { user } (flat, /login) och { data: { user } }
// (envelope, /me). Backend är historiskt inkonsekvent — denna helper
// låter oss läsa båda shapes utan att krasha med "Cannot read properties
// of undefined (reading 'name')" vilket var orsaken till PIN-reload-buggen.
type MaybeWrapped<T> = T | { data: T };
function unwrap<T>(r: MaybeWrapped<T>): T {
  if (r && typeof r === 'object' && 'data' in r) {
    return (r as { data: T }).data;
  }
  return r as T;
}

export async function login(pin: string): Promise<User> {
  const response = await api.post<MaybeWrapped<AuthResponse>>('/auth/login', { pin });
  return normalizeUser(unwrap(response).user);
}

export async function logout(): Promise<void> {
  await api.post<void>('/auth/logout');
}

export async function fetchMe(): Promise<User | null> {
  try {
    const response = await api.get<MaybeWrapped<AuthResponse>>('/auth/me');
    const flat = unwrap(response);
    if (!flat || !flat.user) return null;
    return normalizeUser(flat.user);
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'status' in err) {
      const status = (err as { status: number }).status;
      if (status === 401 || status === 403) return null;
    }
    throw err;
  }
}

// ─── Magic link ────────────────────────────────────────────────────────

// Begär en sign-in-länk till given email. Backend returnerar alltid 200
// (anti-enumeration), så vi diffar bara mellan submit-success och fel.
interface RequestLinkResponse {
  sent: boolean;
  expires_in_seconds: number;
  rate_limited_until: number | null;
}

export async function requestMagicLink(email: string): Promise<RequestLinkResponse> {
  const res = await api.post<MaybeWrapped<RequestLinkResponse>>('/auth/request-link', {
    email,
  });
  return unwrap(res);
}

// Consume en magic-link-token. Backend sätter session-cookie + returnerar
// user + workspaces + valfri redirect-path.
interface VerifyResponse {
  user: User;
  workspaces: Array<{
    id: string;
    slug: string;
    name: string;
    plan: string;
    role: string;
    colorHex?: string | null;
  }>;
  redirectTo: string;
}

export async function verifyMagicLink(
  token: string,
  redirect?: string,
): Promise<VerifyResponse> {
  const query: Record<string, string> = { token };
  if (redirect) query.redirect = redirect;
  const res = await api.get<MaybeWrapped<VerifyResponse>>('/auth/verify', { query });
  const flat = unwrap(res);
  return { ...flat, user: normalizeUser(flat.user) };
}
